// @flow
import groupBy from 'lodash/groupBy';
import countBy from 'lodash/countBy';
import React from 'react';
import Avatar from '@material-ui/core/Avatar';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import { withTheme } from '@material-ui/core';
import { withSnackbar } from 'notistack';
import Input from '@material-ui/core/Input';
import { withTranslation } from 'react-i18next';

import ParticipantAvatar from './ParticipantAvatar';

import { getSocketIfExist } from '../utils/getSocket';

type State = {
  enteringNewTranscriptOwner: boolean,
  newTranscriptOwnerName: string,
};
type Props = {
  displayShortcuts?: boolean,
  maxSpeaker?: number,
  roomSpeakers: Array<Object>,
  scribe?: boolean,
  size?: string,
  speakerListened?: string,
  speakers: Object,
  status: Object,
  t: Function,
  theme: Object,
  user: Object,
};

class ParticipantList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      enteringNewTranscriptOwner: false,
      newTranscriptOwnerName: '',
    };
  }

  addTranscriptOwner = () => {
    const { newTranscriptOwnerName } = this.state;
    this.setState({ newTranscriptOwnerName: '' });
    this.toggleTranscriptOwner();
    const socket = getSocketIfExist();
    if (!socket) {
      return;
    }
    if (!newTranscriptOwnerName) {
      return;
    }
    newTranscriptOwnerName.split(',').forEach((userName: string) => {
      socket.send(
        JSON.stringify({
          type: 'add-transcript-owner',
          userName,
        })
      );
    });
  };

  handleNewTOChange = (e: any) => {
    this.setState({ newTranscriptOwnerName: e.target.value });
  };

  toggleTranscriptOwner = () => {
    const { enteringNewTranscriptOwner } = this.state;
    this.setState({
      newTranscriptOwnerName: '',
      enteringNewTranscriptOwner: !enteringNewTranscriptOwner,
    });
  };

  hasScribe() {
    const { status } = this.props;

    return status && status.scribe && (status.scribe.status === 'pending' || status.scribe.status === 'ongoing');
  }

  render() {
    const {
      displayShortcuts,
      maxSpeaker,
      roomSpeakers,
      scribe,
      size,
      speakerListened,
      speakers,
      status,
      t,
      theme,
      user,
    } = this.props;
    const { enteringNewTranscriptOwner, newTranscriptOwnerName } = this.state;
    const isHost = !!status.host && status.host.avaId === user.avaId;
    const transcriptOwners = roomSpeakers || (status.transcriptOwners || []).map(id => speakers[id] || { id });
    const scribeParticipants = (status.participants || [])
      .filter(p => !status.transcriptOwners.includes(p.avaId) && !p.scribe)
      .map(p => ({
        ...p,
        ...speakers[p.id],
        id: p.avaId,
        userAvatar: p.photoUrl,
        color: undefined,
      }));
    const backgroundColor = theme.palette.secondary[theme.palette.type];
    const counts = countBy(status.participants, 'avaId');

    return (
      <div
        style={{
          height: 52,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <div
          style={{
            overflow: 'scroll',
            display: 'flex',
            paddingBottom: 17,
            boxSizing: 'content-box',
            height: 52,
          }}
          className="invisible-scroll"
        >
          {scribeParticipants.length > 0 && (
            <>
              <div style={{ flexGrow: 1 }} />
              {Object.values(groupBy(scribeParticipants, 'id'))
                .map((ar: any) => ar[0])
                .map((participant, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={index}>
                    <ParticipantAvatar
                      user={participant}
                      nbOfConnection={counts[participant.id]}
                      size={size}
                      badge={scribe}
                    />
                  </div>
                ))}
              <div style={{ flexGrow: 1 }} />
            </>
          )}

          {transcriptOwners.map((participant, index) => (
            // eslint-disable-next-line
            <React.Fragment key={`${participant.id}-${index}`}>
              {(status.participants || []).length > 1 && (
                <div style={{ marginLeft: 2, marginRight: 2 }}>
                  {index < (maxSpeaker || 10000) && (scribe || counts[participant.avaId || participant.id] > 0) && (
                    <>
                      <ParticipantAvatar
                        user={participant}
                        index={index}
                        compact
                        nbOfConnection={counts[participant.avaId || participant.id]}
                        audio={(status.audioStreams || []).find(as => as.avaId === participant.avaId) && true}
                        muted={!(status.audioStreams || []).find(as => as.avaId === participant.avaId && !as.muted)}
                        listened={participant.avaId === speakerListened}
                        canEdit={scribe}
                        canMute={isHost || scribe}
                        size={size}
                        badge={scribe}
                      />
                      {displayShortcuts && (
                        <Avatar
                          aria-label="Recipe"
                          style={{
                            margin: 6,
                            position: 'absolute',
                            top: 15,
                            marginLeft: 15,
                            height: 25,
                            width: 25,
                            backgroundColor: 'rgba(70, 70, 70, 0.8)',
                            color: 'cornsilk',
                          }}
                        >
                          {index <= 8 ? index + 1 : `C${index + 1 - 9}`}
                        </Avatar>
                      )}
                      {displayShortcuts && (
                        <div
                          aria-label="Recipe"
                          style={{
                            margin: 6,
                            position: 'absolute',
                            top: 40,
                            padding: 2,
                            marginLeft: -5,
                            wordBreak: 'break-word',
                            maxWidth: 55,
                            borderRadius: 7,
                            backgroundColor: 'rgba(70, 70, 70, 0.8)',
                            fontSize: 12,
                            color: 'cornsilk',
                          }}
                        >
                          {participant.userName}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              {index === 0 && this.hasScribe() && !scribe && (
                <ParticipantAvatar user={{}} scribe={status.scribe.status} />
              )}
            </React.Fragment>
          ))}
        </div>
        {scribe && transcriptOwners.length > 0 && !enteringNewTranscriptOwner && (
          <Card
            style={{
              backgroundColor,
            }}
          >
            <Button
              onClick={() => this.toggleTranscriptOwner()}
              style={{
                width: 50,
                height: 52,
                padding: 'inherit',
                fontSize: 30,
              }}
            >
              +
            </Button>
          </Card>
        )}
        {scribe && transcriptOwners.length > 0 && enteringNewTranscriptOwner && (
          <Card
            style={{
              backgroundColor,
              display: 'flex',
              flexDirection: 'column',
              height: 75,
            }}
          >
            <form>
              <Input
                value={newTranscriptOwnerName}
                onChange={this.handleNewTOChange}
                autoFocus
                style={{
                  height: 25,
                  flexGrow: 1,
                }}
              />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <Button
                  onClick={() => this.toggleTranscriptOwner()}
                  style={{
                    flexGrow: 1,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => this.addTranscriptOwner()}
                  type="submit"
                  style={{
                    flexGrow: 1,
                  }}
                  variant="contained"
                >
                  Create
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    );
  }
}

export default withTranslation()(withSnackbar(withTheme(ParticipantList)));
